import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  withStyles,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import StaffCanvasAccessRowContainer from '../StaffCanvasAccessRow/StaffCanvasAccessRowContainer';
import StaffCanvasAccessBulkBarContainer from '../StaffCanvasAccessBulkBar/StaffCanvasAccessBulkBarContainer';
import useStaffCanvasAccessHook from './useStaffCanvasAccessHook';
import { buildIneligibleCanvasAccessMessage } from '../utils/canvas-access.utils';
import styles from './StaffCanvasAccessTab.style';

/**
 * Staff profile tab for viewing and managing per-location Canvas admin access.
 */
const StaffCanvasAccessTab = ({ classes, personId = null }) => {
  const {
    bulkApi,
    bulkBarContainerProps,
    editingLocationKey,
    eligibility,
    error,
    errorMessage,
    loading,
    locations,
    onCancelRowEdit,
    onLoadRolesForLocation,
    onRowDraftRoleChange,
    onSaveRowEdit,
    onStartRowEdit,
    onToggleRowSelected,
    refresh,
    roleOptionsByLocation,
    rolesLoadingLocationKey,
    rowActionsDisabled,
    showBulkBar,
    submitting,
    summary,
  } = useStaffCanvasAccessHook(personId);

  const isInitialLoading = loading && locations.length === 0 && !eligibility;
  const isIneligible = eligibility?.status === 'INELIGIBLE';
  const ineligibleMessage = buildIneligibleCanvasAccessMessage(eligibility);
  const isEmpty = !loading && locations.length === 0 && !isIneligible;

  return (
    <Box className={classes.root} data-testid="staff-canvas-access-tab">
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="body2" color="textSecondary">
          {summary}
        </Typography>
        <IconButton aria-label="Refresh Canvas access" onClick={refresh} disabled={loading || submitting}>
          {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
        </IconButton>
      </Box>

      {error && (
        <Typography variant="body2" color="error" className={classes.error}>
          {errorMessage}
        </Typography>
      )}

      <StaffCanvasAccessBulkBarContainer {...bulkBarContainerProps} visible={showBulkBar} />

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>Location</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Canvas role</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isInitialLoading && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <CircularProgress size={24} />
              </TableCell>
            </TableRow>
          )}

          {isIneligible && ineligibleMessage && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography variant="body2" color="textSecondary">
                  {ineligibleMessage}
                </Typography>
              </TableCell>
            </TableRow>
          )}

          {isEmpty && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography variant="body2" color="textSecondary">
                  No locations found.
                </Typography>
              </TableCell>
            </TableRow>
          )}

          {!isInitialLoading && !isEmpty && !isIneligible && locations.map((row) => (
            <StaffCanvasAccessRowContainer
              key={row.locationKey}
              row={row}
              editingLocationKey={editingLocationKey}
              roleOptionsByLocation={roleOptionsByLocation}
              rowActionsDisabled={rowActionsDisabled}
              rolesLoadingLocationKey={rolesLoadingLocationKey}
              submitting={submitting}
              bulkApi={bulkApi}
              onLoadRolesForLocation={onLoadRolesForLocation}
              onStartRowEdit={onStartRowEdit}
              onCancelRowEdit={onCancelRowEdit}
              onSaveRowEdit={onSaveRowEdit}
              onToggleRowSelected={onToggleRowSelected}
              onRowDraftRoleChange={onRowDraftRoleChange}
            />
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

StaffCanvasAccessTab.propTypes = {
  personId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default withStyles(styles)(StaffCanvasAccessTab);
